import { motion } from 'framer-motion';
import { ScrambleText } from '@/components/ScrambleText';
import { MapPin, ExternalLink } from 'lucide-react';

const CODE_LINE_3 = 'space.init({m2:300,screens:20,projection:true});venue.restore(barn,100_years);location.set("Poland")';

const features = [
  { label: 'EXHIBITION SPACE', value: '300 M\u00B2' },
  { label: 'DIGITAL SCREENS', value: '20' },
  { label: 'FROM BERLIN', value: '1.5 HRS' },
  { label: 'VENUE AGE', value: '100 YRS' },
];

export default function SpaceSection() {
  return (
    <section
      data-testid="space-section"
      className="relative z-10 px-6 sm:px-10 py-20 lg:py-28"
    >
      <div className="overflow-hidden mb-16">
        <div className="code-divider-scroll whitespace-nowrap text-[10px] font-mono text-[#1a1a1a] select-none">
          {CODE_LINE_3} {CODE_LINE_3} {CODE_LINE_3} {CODE_LINE_3}
        </div>
      </div>

      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.7 }}
        >
          <ScrambleText
            text="SPACE / PLACE"
            className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight uppercase text-white font-outfit block"
            as="h2"
            duration={800}
            distortOnHover
          />
        </motion.div>

        {/* Venue image */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="mt-12 relative overflow-hidden border border-[#222222] group/venue"
        >
          <img
            src="/images/venue.jpeg"
            alt="Leniwka Stary Mlyn"
            className="w-full h-[300px] sm:h-[400px] object-cover opacity-50 grayscale group-hover/venue:opacity-100 group-hover/venue:grayscale-0 transition-all duration-700"
            data-testid="venue-image"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/70 via-[#050505]/20 to-transparent group-hover/venue:from-[#050505]/40 group-hover/venue:via-transparent transition-all duration-700" />
          <div className="absolute bottom-6 left-6 sm:left-10">
            <p className="text-xs font-mono text-[#f6f6f6] tracking-[0.2em]">
              LENIWKA STARY MLYN
            </p>
            <p className="text-[10px] font-mono text-[#f6f6f6] tracking-[0.15em] mt-1">
              CLOSE TO SWIEBODZIN, POLAND
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.6, delay: 0.25 }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 mt-12"
        >
          <div className="space-y-5">
            <p className="text-sm sm:text-base leading-relaxed font-mono text-[#f6f6f6]">
              THE RELATIVE GALLERY CAN BE FOUND WITHIN LENIWKA STARY MLYN,
              A BESPOKE BOUTIQUE HOTEL.
            </p>
            <p className="text-sm sm:text-base leading-relaxed font-mono text-[#f6f6f6]">
              THE MAIN EXHIBITION SPACE IS HOUSED WITHIN A HISTORICAL
              100-YEAR-OLD RESTORED BARN, TRANSFORMING A HISTORIC RURAL
              STRUCTURE INTO A CONTEMPORARY SPACE FOR DIGITAL CULTURE.
            </p>
            <ul className="space-y-2 mt-4">
              {[
                '20 DIGITAL SCREENS SHOWCASING AI AND DIGITAL ARTWORKS',
                'PROJECTION MAPPING / IMMERSIVE VISUAL ENVIRONMENT',
                'ADAPTABLE EXHIBITION LAYOUT',
                'COUNTRYSIDE SETTING',
              ].map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-3 text-xs sm:text-sm font-mono text-[#f6f6f6]"
                >
                  <span className="text-[#f6f6f6] mt-1">//</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Stats grid */}
          <div>
            <div className="grid grid-cols-2 gap-px border border-[#222222] self-start">
              {features.map((f) => (
                <div
                  key={f.label}
                  data-testid={`space-stat-${f.label.toLowerCase().replace(/\s/g, '-')}`}
                  className="p-5 sm:p-6 border border-[#222222] bg-[#0a0a0a] hover:bg-[#111111] transition-colors duration-300"
                >
                  <span className="text-[10px] tracking-[0.25em] font-mono text-[#f6f6f6] block mb-2">
                    {f.label}
                  </span>
                  <span className="text-lg sm:text-xl font-outfit text-white font-semibold">
                    {f.value}
                  </span>
                </div>
              ))}
            </div>

            {/* Directions link */}
            <a
              href="https://maps.app.goo.gl/gAokTGHa5G399WZT8"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="check-directions-link"
              className="group/dir flex items-center justify-center gap-2 mt-4 py-3.5 border border-[#222222] bg-[#0a0a0a] hover:bg-white hover:text-black transition-all duration-400 cursor-pointer"
              style={{ cursor: 'pointer' }}
            >
              <MapPin className="w-3.5 h-3.5 text-[#f6f6f6] group-hover/dir:text-black transition-colors duration-400" />
              <span className="text-[11px] font-mono tracking-[0.15em] uppercase text-[#f6f6f6] group-hover/dir:text-black transition-colors duration-400">
                Check Directions to the Gallery
              </span>
              <ExternalLink className="w-3 h-3 text-[#f6f6f6] group-hover/dir:text-black transition-colors duration-400" />
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
